import { config } from 'config';
import { RestApiMethodName } from 'lib/enum/rest-api-method';
import { HttpResponseError, XMLHttpRequestError } from 'lib/error/http-response-error';
import { AbortError } from 'p-retry';
import { ABORT_CONTROLLER_SIGNAL_ABORTED_MESSAGE } from '../config';
import { PublicApi } from './PublicApi';
import { createHttpResponseError, parseJSON, checkStatus, abortRetrying, parseSettings } from './utils';
import queryString from 'query-string';
import { getTokenPrefix, TokenPrefix } from 'lib/utils/embedded-app.utils';

export class PrivateApi extends PublicApi {
    private getAccessTokenSilently: () => Promise<string>;

    private readonly tokenPrefix: TokenPrefix = getTokenPrefix();

    constructor() {
        super();
        this.getAccessTokenSilently = async (): Promise<string> => '';
    }

    setAccessTokenSilentlyGetter(getAccessTokenSilently: () => Promise<string>): void {
        this.getAccessTokenSilently = getAccessTokenSilently;
    }

    protected parseEndpoint(endpoint: string, params?: any): string {
        const url = endpoint.indexOf('http') === 0 ? endpoint : config.apiUrl + endpoint;
        const querystring = params ? `?${queryString.stringify(params, { arrayFormat: 'bracket' })}` : '';
        return `${url}${querystring}`;
    }

    protected async apiRequest<T>(
        endpoint: string,
        { params, ...settings }: any = {},
        expectNoResponse?: boolean,
    ): Promise<T> {
        try {
            const accessToken = await this.getAccessTokenSilently();
            const requestHeaders = endpoint.includes(config.mrWorkWebsiteBaseUrl)
                ? settings.headers
                : {
                      ...settings.headers,
                      // eslint-disable-next-line @typescript-eslint/naming-convention
                      'Web-App-Version': config.appVersion,
                  };
            const headers = accessToken
                ? { ...requestHeaders, Authorization: `${this.tokenPrefix} ${accessToken}` }
                : requestHeaders;
            const response = await fetch(this.parseEndpoint(endpoint, params), parseSettings({ ...settings, headers }));

            const shouldAbortRetrying =
                settings.method === RestApiMethodName.GET && (response.status === 500 || response.status !== 200);

            if (shouldAbortRetrying) return (await abortRetrying(response)) as any;

            const checkedResponse = await checkStatus(response);
            if (settings.returnRawResponse !== 'undefined' && settings.returnRawResponse === true) {
                return checkedResponse as any;
            }

            if (expectNoResponse) return checkedResponse as T;

            return await parseJSON(checkedResponse);
        } catch (err: any) {
            if (err?.originalError?.response?.url) {
                const url = err.originalError.response.url;
                const uuidv4Regex = /[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;
                const talentUrlPattern = new RegExp(`/api/talents/${uuidv4Regex.source}$`);
                if (talentUrlPattern.test(url)) {
                    return Promise.reject(
                        createHttpResponseError('Talent deleted', { status: 913 } as any, {
                            message: 'Talent deleted',
                        }),
                    );
                }
            }

            if (settings?.signal?.aborted) {
                throw new Error(ABORT_CONTROLLER_SIGNAL_ABORTED_MESSAGE);
            }

            if (err instanceof HttpResponseError || err instanceof AbortError) {
                return Promise.reject(err);
            }
            if (err?.error === 'login_required') {
                return Promise.reject(
                    createHttpResponseError('Login required', { status: 944 } as any, { message: 'Login required' }),
                );
            }

            const errorMessage = 'Unknown error occurred';
            return Promise.reject(
                createHttpResponseError(errorMessage, { status: null } as any, { message: errorMessage }),
            );
        }
    }

    public postMultipart<T>(
        endpoint: string,
        data: FormData,
        expectNoResponse?: boolean,
        onProgressChange?: (e: ProgressEvent<XMLHttpRequestEventTarget>) => void,
    ): Promise<T> {
        return new Promise(async (resolve, reject) => {
            const request = new XMLHttpRequest();
            request.open(RestApiMethodName.POST, this.parseEndpoint(endpoint));

            onProgressChange && request.upload.addEventListener('progress', onProgressChange);

            request.onload = (): void => {
                if (request.status >= 200 && request.status < 300) {
                    if (expectNoResponse) {
                        resolve({} as T);
                    } else {
                        resolve(JSON.parse(request.response));
                    }
                }
                const contentType = request.getResponseHeader('Content-Type');
                const server = request.getResponseHeader('Server');
                const error = new XMLHttpRequestError(request.response);
                error.contentType = contentType;
                error.server = server;
                reject(error);
            };
            request.onerror = (): void => {
                const contentType = request.getResponseHeader('Content-Type');
                const server = request.getResponseHeader('Server');
                const error = new XMLHttpRequestError(request.response);
                error.contentType = contentType;
                error.server = server;
                reject(error);
            };

            const accessToken = await this.getAccessTokenSilently();
            accessToken && request.setRequestHeader('Authorization', `${this.tokenPrefix} ${accessToken}`);

            request.send(data);
        });
    }
}

export const privateApi = new PrivateApi();
